import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import FixedTooltip from './FixedTooltip';

const PORTAL_MOUNT_EL = window.document.body;

const fixedTooltipPortalDefaultPropPortalMountRef = {};
/** @param {any} props */
export default function FixedTooltipPortal({
  isVisible,
  className = null,
  wrapperClassName = null,
  onCloseClick,
  children,
  style = null,
  shouldHideCloseButton = false,
  portalMountRef = fixedTooltipPortalDefaultPropPortalMountRef
}) {
  const [isOpen, setIsOpen] = useState(false);

  function handleCloseClick(e) {
    // Prevent clicks on the portal backdrop from bubbling to underlying clickable elements
    e.preventDefault();
    e.stopPropagation();
    onCloseClick();
  }

  useEffect(() => {
    if (isVisible) {
      PORTAL_MOUNT_EL.classList.add('no-scroll');
      return () => PORTAL_MOUNT_EL.classList.remove('no-scroll');
    }
    return PORTAL_MOUNT_EL.classList.remove('no-scroll');
  }, [isVisible]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsOpen(isVisible);
    }, 100);

    return () => clearTimeout(timeout);
  }, [isVisible]);

  if (portalMountRef?.current === null) return null;

  return createPortal(
    <>
      {isVisible && (
        <div
          role="button"
          tabIndex="-1"
          onKeyPress={null}
          onClick={handleCloseClick}
          className="fixed bg-black-70 absolute--fill z-max"
        />
      )}
      <div
        data-test-id="tooltip"
        className={classnames(
          'FixedTooltipWrapper__tooltip z-max fixed transition bottom-0 left-0 right-0',
          wrapperClassName,
          {
            'FixedTooltipWrapper__tooltip--hidden': !isOpen
          }
        )}
      >
        {isVisible && (
          <FixedTooltip
            className={className}
            onCloseClick={onCloseClick}
            title={children}
            style={style}
            shouldHideCloseButton={shouldHideCloseButton}
          />
        )}
      </div>
    </>,
    portalMountRef?.current || PORTAL_MOUNT_EL
  );
}

FixedTooltipPortal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
  onCloseClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  shouldHideCloseButton: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  portalMountRef: PropTypes.object
};
